import type { RepositoryIntegration, RepositoryType, Tool } from '../../utils/api-client/api-schemas'
import { isAiScannerTool, isSbomTool } from '../../utils/detector-utils.ts'
import { convertToolToIntegrationType } from '../../utils/repository-utils.tsx'

/** GitLab tools that derive integration status from the repository SCM type, not from RepositoryIntegration records. */
export type GitLabTool = 'gitlab_sast' | 'gitlab_dependency_scanning'

export type ToolIntegrationStatus = 'configured' | 'none'

/**
 * Determines the integration status for a tool.
 * GitLab tools are "configured" when the repository is a GitLab repo (integration is at the SCM level).
 * Other tools check the repository integrations list.
 */
export const getToolIntegrationStatus = (
  tool: Tool,
  integrations: RepositoryIntegration[],
  repositoryType?: RepositoryType
): ToolIntegrationStatus => {
  if (isGitLabTool(tool)) {
    return repositoryType === 'gitlab' ? 'configured' : 'none'
  }
  const integrationType = convertToolToIntegrationType(tool)
  if (!integrationType) {
    return 'none'
  }
  const hasIntegration = integrations.some(i => i.type === integrationType)
  return hasIntegration ? 'configured' : 'none'
}

/**
 * Workflow path for a selected tool.
 * - 'upload-only': no integration, user must upload a file
 * - 'import-only': integration exists but tool only supports import
 * - 'choice': integration exists and tool supports both upload and import
 */
export type WorkflowPath = 'upload-only' | 'import-only' | 'choice'

/**
 * Tools the platform can import findings from without an uploaded file.
 *
 * Mirrors the `NATIVE_TOOLS` constant in
 * `pixee-platform-rest/src/main/java/ai/pixee/platform/api/scans/ValidCreateScanRequest.java`.
 * The platform rejects a fileless scan for any tool outside that set with
 * "Native integration is not supported for tool <tool>", so offering the import
 * option for one of them can only ever produce an error.
 *
 * There is no API that exposes this list (see https://github.com/pixee/product-os/pull/79
 * for why), so the two must be kept in sync by hand: a tool gaining or losing a
 * native integration on the backend requires the same edit here.
 */
const NATIVE_TOOLS: ReadonlySet<Tool> = new Set([
  'arnica_sast',
  'black_duck_hub_sca',
  'checkmarx',
  'checkmarx_enterprise',
  'codeql',
  'datadog_sast',
  'dependabot',
  'gitlab_dependency_scanning',
  'gitlab_sast',
  'polaris_sast',
  'semgrep',
  'snyk',
  'sonar',
  'veracode',
])

/**
 * Native tools whose import cannot fall back to a system default instance.
 *
 * Every other native tool resolves an unmapped repository against the deployment's default instance,
 * so offering import without an explicit mapping still reaches something. Black Duck Hub and
 * Checkmarx Enterprise have no system instance at all — which is also why theirs are the repository
 * mappings whose `integration_id` is required — so `resolvePullTarget` finds nothing and the import
 * answers 400.
 * Offering the choice there can only produce an error, so it is withheld until the binding exists.
 */
const MAPPING_REQUIRED_TOOLS: ReadonlySet<Tool> = new Set(['black_duck_hub_sca', 'checkmarx_enterprise'])

/**
 * The workflow a selected tool offers.
 *
 * `toolIntegrationStatus` is optional because only the caller holding the repository's mappings can
 * supply it. Callers that have form values alone omit it and get the tool's unconditional answer;
 * that is sound because the radio they would render is the only way to reach `import`, and its
 * default is `upload` — so a suppressed choice and an unsupplied status agree on the outcome.
 */
export function resolveWorkflowPath(
  tool: Tool | undefined,
  toolIntegrationStatus?: ToolIntegrationStatus
): WorkflowPath | undefined {
  if (!tool) {
    return undefined
  }
  // The Pixee AI Scanner generates findings server-side, so there is never a file to upload and no
  // external tool to import from — it is a "Generate Findings" action (ENH-590). Force import-only so
  // the SARIF file input is hidden; the drawer branches on the tool at submit to call the AI scan.
  if (isAiScannerTool(tool)) {
    return 'import-only'
  }
  if (!NATIVE_TOOLS.has(tool)) {
    return 'upload-only'
  }
  if (
    MAPPING_REQUIRED_TOOLS.has(tool) &&
    toolIntegrationStatus !== undefined &&
    toolIntegrationStatus !== 'configured'
  ) {
    return 'upload-only'
  }
  return 'choice'
}

/**
 * SBOM tools the platform reads the bill of materials for itself, from the SCM.
 *
 * A fileless SBOM request for any other tool is rejected. Like `NATIVE_TOOLS`, no API exposes this
 * list, so it must be kept in sync with the backend by hand.
 */
const PLATFORM_FETCHED_SBOM_TOOLS: ReadonlySet<Tool> = new Set(['dependabot', 'gitlab_dependency_scanning'])

/**
 * Whether the customer has to supply the SBOM file themselves.
 *
 * An SCA scan is never analyzed without an SBOM at its commit, so a tool the platform cannot read
 * one for needs the file even when its findings are imported natively. Black Duck Hub is the case
 * that motivates this: it exposes the bill of materials as findings but not as an SBOM.
 */
export const getIsSbomFileRequired = (tool: Tool | undefined): boolean =>
  !!tool && isSbomTool(tool) && !PLATFORM_FETCHED_SBOM_TOOLS.has(tool)

/**
 * Whether a tool is a GitLab pipeline tool that derives integration status from the repository SCM type.
 */
export const isGitLabTool = (tool: Tool): tool is GitLabTool =>
  tool === 'gitlab_sast' || tool === 'gitlab_dependency_scanning'

/**
 * Validates a commit SHA is a 7-40 character hexadecimal string.
 * Returns an error message if invalid, undefined if valid or empty.
 */
export const validateCommitSha = (sha: string): string | undefined => {
  if (sha === '') return undefined
  if (!/^[0-9a-fA-F]{7,40}$/.test(sha)) {
    return 'Commit hash must be a 7-40 character hexadecimal string'
  }
  return undefined
}
